import { Box, Grid, Link } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DataGrid, GridColumnProps } from '@roc/ui';
import { noOpComparator } from '@roc/ui/utils';
import { observer } from 'mobx-react';
import { ReactNode } from 'react';
import { useDebtTrackingStore } from '../../hooks/useDebtTrackingStore';
import { formatDisplayDate, formatDisplayDateTime, formatMoney } from '../../utils/debtTrackingUtils';

const useStyles = makeStyles(() => ({
  gridContainer: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    height: '60vh',
    overflow: 'hidden',
  },
}));

// One row per loan with its CURRENT financing amount (each change replaces it; the loan dialog shows the history).
const columns: GridColumnProps[] = [
  {
    field: 'loanId',
    headerName: 'Loan ID',
    minWidth: 100,
    maxWidth: 100,
    cellRenderer: 'loanIdCellRenderer',
    comparator: noOpComparator,
  },
  {
    field: 'financingAmount',
    headerName: 'Financing Amount',
    minWidth: 150,
    cellRenderer: 'currencyCellRenderer',
    type: 'numericColumn',
    comparator: noOpComparator,
  },
  {
    field: 'effectiveDate',
    headerName: 'Effective Date',
    minWidth: 150,
    maxWidth: 160,
    cellRenderer: 'dateCellRenderer',
    comparator: noOpComparator,
  },
  {
    field: 'lastComment',
    headerName: 'Last Comment',
    minWidth: 220,
    flex: 2,
    cellRenderer: 'lastCommentCellRenderer',
    comparator: noOpComparator,
  },
  {
    field: 'lastUpdatedBy',
    headerName: 'Last Updated By',
    minWidth: 180,
    comparator: noOpComparator,
  },
  {
    field: 'lastUpdatedAt',
    headerName: 'Last Updated',
    minWidth: 190,
    maxWidth: 200,
    cellRenderer: 'dateTimeCellRenderer',
    comparator: noOpComparator,
  },
];

export const DebtTrackingGrid = observer(({ toolbar }: { toolbar: ReactNode }) => {
  const classes = useStyles();
  const { debtTrackingStore } = useDebtTrackingStore();
  const { loansGridStore } = debtTrackingStore;

  const frameworkComponents = {
    loanIdCellRenderer: ({ value }) => value ? (
      <Link component="button" variant="body2" onClick={() => debtTrackingStore.openLoanDetail(value)}>
        <strong>{value}</strong>
      </Link>
    ) : null,
    currencyCellRenderer: ({ value }) => (value === undefined ? '' : formatMoney(value)),
    dateCellRenderer: ({ value }) => formatDisplayDate(value) || '-',
    dateTimeCellRenderer: ({ value }) => formatDisplayDateTime(value) || '-',
    lastCommentCellRenderer: ({ value, data }) => data ? (value || <em>No comment</em>) : null,
  };

  // Sorting and paging happen on the server (assetm-service); the grid only shows the current page.
  const onSortChanged = params => loansGridStore.setSortModel(params.api.getSortModel());

  return (
    <Box>
      <Grid item className={classes.gridContainer}>
        <DataGrid
          columns={columns}
          rows={loansGridStore.gridData.data.rows}
          frameworkComponents={frameworkComponents}
          toolbar={toolbar}
          onSortChanged={onSortChanged}
          isLoading={loansGridStore.isLoading}
          isError={loansGridStore.isError}
          showFilters={false}
          sortModel={loansGridStore.sortModel}
          paginationData={loansGridStore.gridData.meta}
          setPageNumber={loansGridStore.setPageNumber}
          getRowNodeId={data => data.loanId}
        />
      </Grid>
    </Box>
  );
});
